import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DocumentSigner } from '../../../../../../contexts/DocumentContext';
import Tooltip from '../../../../../tooltip';
import styles from './styles.module.scss';

interface SignerProps {
  id: string;
  signedAt: string;
  email: string;
  order: number;
}

interface Props {
  signers?: DocumentSigner[] | null;
  useSignerOrder?: boolean;
}

enum SigningStatus {
  SIGNED = 'Signed',
  NOTIFIED = 'Notified',
  PENDING = 'Pending',
}

export const SignersTooltipTable = ({ signers, useSignerOrder = true }: any) => {
  const signersList = signers?.map((signer: SignerProps, index: any, arr: any) => {
    const isSigned = signer?.signedAt;

    // For "send all at once" mode (useSignerOrder = false/0/null/undefined), all signers are notified simultaneously
    // For sequential mode (useSignerOrder = true/1), only notify if this is the first signer OR previous signer signed
    const isNotified = !useSignerOrder // Handles false, 0, null, undefined as "all at once"
      ? true // All signers notified in "send all at once" mode
      : index === 0 || !!arr[index - 1]?.signedAt; // Sequential logic: first signer OR previous signed

    return (
      <div className={styles.signersContainer} key={signer.email}>
        <div className={styles.signerData}>{signer?.email}:</div>
        <div className={styles[isSigned ? 'signerStatus__signed' : 'signerStatus']}>
          {isSigned ? SigningStatus.SIGNED : isNotified ? SigningStatus.NOTIFIED : SigningStatus.PENDING}
        </div>
      </div>
    );
  });

  return (
    <div className={styles.signersTooltipContainer} style={{ minWidth: '240px' }}>
      {signersList}
    </div>
  );
};

export const signersCounter = ({
  hasSignedNumber,
  totalSigners,
}: {
  hasSignedNumber: number;
  totalSigners: number;
}) => (
  <span className={styles.signersBadgeContainer__signersCounter}>
    ({hasSignedNumber}/{totalSigners})
  </span>
);

export const SigningProcessBadgeContainer = ({ signers, useSignerOrder = true }: Props) => {
  const sortedSigners = signers
    ? [...signers].sort((a, b) => {
        return a?.order - b?.order;
      })
    : [];
  const totalSigners = signers?.length || 0;
  const hasSignedNumber = sortedSigners?.filter((signer: SignerProps) => !!signer.signedAt).length || 0;

  return (
    <>
      <div className={styles.signersBadgeContainer}>
        <span>
          <FormattedMessage id="document-signing.badge" defaultMessage="Signing" />
        </span>
        <span className={styles.signersBadgeContainer__counter}>
          <Tooltip
            testId="internal-signers-counter"
            icon={signersCounter({ hasSignedNumber, totalSigners })}
            placement="bottom-end"
          >
            <SignersTooltipTable signers={signers} useSignerOrder={useSignerOrder} />
          </Tooltip>
        </span>
      </div>
    </>
  );
};
